<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div> 
      </div>

      <div class="modal__main-icon modal__main-icon--transaction"></div>
      <div class="modal__close" @click="closeModal()"></div>
      <div class="modal__title">{{ $t(`addTransaction.${"bonusTitle"}`) }}</div>

      <form class="form" @submit.prevent="createTransaction()">
        <div class="add__form">

          <transition name="fade" mode="out-in">
            <div class="add__content">
    
              <div class="form__field">
                <VSelect class="select select--default" :class="{ 'select--error': userHasError }" :options="agents"
                  :reduce="(agent) => agent.id" :placeholder="$t('addTransaction.agent')" :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`
                    " v-model="userId" />
                <div class="form__error" v-if="userHasError">
                  {{ $t(`addTransaction.${"agentErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field">
                <VSelect class="select select--default" :class="{ 'select--error': transactionHasError }"
                  :options="transactionTypes" :reduce="(transaction) => transaction.id"
                  :placeholder="$t('addTransaction.transactionType')" label="name" v-model="transactionTypeId" />
                <div class="form__error" v-if="transactionHasError">
                  {{ $t(`addTransaction.${"transactionErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field">
                <div class="modal__report-price">
                  <div class="modal__report-prefix">{{ prefixSign }}</div>
                  <input type="text" class="input input--default" :class="{ 'input--error': amountHasError }"
                    v-model="amount" />
                  <div class="modal__report-label">.-CHF</div>
                  <div class="modal__report-popup">
                    <div class="modal__report-icon"></div>
                    <div class="modal__report-text">
                      {{ $t('addTransaction.withdrawlsMsg') }}
                    </div>
                  </div>
                </div>
                <div class="form__error" v-if="amountHasError">
                  {{ amountErrorPlaceholder }}
                </div>
              </div>
              <div class="form__field">
                <textarea class="input input--default" :class="{ 'input--error': descriptionHasError }" id="" cols="30"
                  rows="10" :placeholder="$t('addTransaction.description')" v-model="description"></textarea>
                <div class="form__error" v-if="descriptionHasError">
                  {{ $t(`addTransaction.${"descriptionErrorMsg"}`) }}
                </div>
              </div>
              <div class="form__field"> 
                <Datepicker class="input input--default  vs__search" :placeholder="$t('addTransaction.date')" v-model="todaysDate"
                  :config="statics.datePickerConfig" />
              </div>

              <div v-if="isRequesting"
                class="button modal__button modal__button--submit button--default button--loading"></div>
              <button type="submit" v-else @click="createTransaction()"
                class="button modal__button modal__button--submit button--default">
                {{ $t(`addTransaction.${"saveBtn"}`) }}
              </button>
            </div>
          </transition>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import moment from "moment";
import statics from "@/statics";

defineOptions({
  name: "AddTransactionModal"
});

const store = useStore();
const route = useRoute();

// Reactive Local State
const todaysDate = ref(null);
const displayTime = ref(moment().format("H:mm"));
const isRequesting = ref(false);
const userId = ref("");
const description = ref("");
const transactionTypeId = ref("");
const amount = ref(0);
const prefixSign = ref("");

const userHasError = ref(false);
const transactionHasError = ref(false);
const amountHasError = ref(false);
const descriptionHasError = ref(false);
const amountErrorPlaceholder = ref("");

const teamId = ref(null);

let timeIntervalId = null;

// Computed Mappings (Replaces mapGetters)
const reportForms = computed(() => store.getters.reportForms);

const transactionTypes = computed(() => {
  return reportForms.value ? reportForms.value["transaction_types"] : [];
});

const agents = computed(() => {
  return reportForms.value ? reportForms.value.agents : [];
});

// Input Validation Methods
const validateAgent = () => {
  userHasError.value = !userId.value;
};

const validateTransaction = () => {
  transactionHasError.value = !transactionTypeId.value;
};

const validateAmount = () => {
  if (amount.value) {
    if (amount.value > 0) {
      amountHasError.value = false;
      amountErrorPlaceholder.value = "";
    } else {
      if (transactionTypeId.value > 2) {
        amountHasError.value = true;
        amountErrorPlaceholder.value = "Invalid amount.";
      } else {
        amountHasError.value = false;
        amountErrorPlaceholder.value = "";
      }
    }
  } else {
    amountHasError.value = true;
    amountErrorPlaceholder.value = "Please enter an amount.";
  }
};

const validateDescription = () => {
  descriptionHasError.value = !description.value;
};

// State Synchronization Watchers
watch(userId, () => {
  validateAgent();
});

watch(transactionTypeId, (typeId) => {
  validateTransaction();

  if (typeId && transactionTypes.value) {
    const match = transactionTypes.value.find((t) => t.id === typeId);
    if (match) {
      prefixSign.value = match.is_deposit ? "-" : "+";
    }
  }
});

watch(amount, () => {
  validateAmount();
});

watch(description, () => {
  validateDescription();
});

// UI Actions Boundaries
const closeModal = () => {
  store.commit("setShowAddTransactionModal", false);
};

const checkTime = () => {
  const currentFormat = moment().format("H:mm");
  if (displayTime.value !== currentFormat) {
    displayTime.value = currentFormat;
  }
};

const createTransaction = async () => {
  isRequesting.value = true;
  validateAgent();
  validateTransaction();
  validateAmount();
  validateDescription();

  if (
    !userHasError.value &&
    !transactionHasError.value &&
    !amountHasError.value &&
    !descriptionHasError.value
  ) {
    try {
      await store.dispatch("createTransaction", {
        user_id: userId.value,
        description: description.value,
        transaction_type_id: transactionTypeId.value,
        amount: amount.value,
        team_id: teamId.value,
        date: todaysDate.value
      });

      if (
        Number(route.query.userId) &&
        (route.query.paidStatus === "true" || route.query.paidStatus === "false")
      ) {
        await store.dispatch("getReports", teamId.value);
      }
      closeModal();
    } catch (err) {
      console.error(err);
    }
  }
  isRequesting.value = false;
};

// Lifecycle Implementations
onMounted(async () => {
  if (route.query.userId) {
    userId.value = Number(route.query.userId);
  }

  if (route.query.teamId) {
    teamId.value = Number(route.query.teamId);
  }

  await store.dispatch("getTeams");
  timeIntervalId = setInterval(checkTime, 1000);

  todaysDate.value = new Date().toISOString().split("T")[0];
});

onUnmounted(() => {
  if (timeIntervalId) {
    clearInterval(timeIntervalId);
  }
});
</script>

<style lang="scss" scoped>
textarea::placeholder {
  color: var(--text-2) !important;
}

.form {
  margin-top: 70px !important;
  height: calc(100% - 70px - 18px) !important;
}

.add {
  &__form {
    width: initial !important;
    height: calc(100% - 16px - 30px) !important;
    padding: 0 30%;
    overflow: auto;
  }

  &__content {
    overflow: initial !important;
  }
}

.modal {
  &__title {
    font-size: var(--medium);
    color: var(--text-2);
    margin-left: 74px;
  }

  textarea {
    max-width: 100%;
    min-height: 100px;
    padding-top: 10px;
  }

  &__report {
    &-price {
      position: relative;

      .input {
        text-align: right;
        padding-right: 51%;
      }
    }

    &-price .input:hover~&-popup {
      opacity: 1;
    }

    &-label {
      left: 50%;
      pointer-events: none;
    }

    &-time {
      display: flex;
      align-items: center;
    }

    &-prefix,
    &-label {
      color: var(--text-8) !important;
      top: 50%;
      transform: translateY(-50%);
    }

    &-prefix,
    &-popup,
    &-label {
      position: absolute;
    }

    &-prefix {
      left: 15px;
    }

    &-popup {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 246px;
      height: 100%;
      padding: 5px 10px;
      box-shadow: 0px 3px 6px var(--shadow-3);
      top: 0;
      right: -100%;
      margin-right: 25px;
      border-radius: var(--radius-1);
      opacity: 0;
      pointer-events: none;
    }

    &-icon {
      min-height: 18px;
      min-width: 18px;
      background: url("../assets/icons/default/info_icon.svg") no-repeat center;
      background-size: cover;
    }

    &-text {
      color: var(--text-2);
      font-size: var(--x-smaller);
      padding-left: 5px;
    }
  }
}
</style>
